import { Injectable, inject } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreateHomePageBottomBannerInput,
    ModelHomePageBottomBannerFilterInput,
    HomePageBottomBanner,
    UpdateHomePageBottomBannerInput,
    DeleteHomePageBottomBannerInput
} from '@app/core/services/api/API.service';
import { AmplifyApiService } from './amplify-api.service';

type ListGraphqlAuthMode = 'apiKey' | 'userPool';

@Injectable({
    providedIn: 'root'
})
export class HomePageBottomBannerService {
    private _amplifyApiService = inject(AmplifyApiService);
    private amplifyClient = generateClient();
    private _cachedBanners: Map<string, HomePageBottomBanner[]> = new Map();
    private _isLoading: Map<string, boolean> = new Map();
    private _loadingPromises: Map<
        string,
        Promise<HomePageBottomBanner[]>
    > = new Map();

    constructor() {}

    private _getCacheKey(
        filter: ModelHomePageBottomBannerFilterInput,
        authMode?: ListGraphqlAuthMode
    ): string {
        return JSON.stringify({
            filter: filter || {},
            authMode: authMode ?? 'default'
        });
    }

    async listHomePageBottomBanners(
        filter: ModelHomePageBottomBannerFilterInput = null,
        authMode?: ListGraphqlAuthMode
    ): Promise<HomePageBottomBanner[]> {
        const cacheKey = this._getCacheKey(filter, authMode);

        if (this._cachedBanners.has(cacheKey)) {
            return this._cachedBanners.get(cacheKey);
        }

        if (
            this._isLoading.get(cacheKey) &&
            this._loadingPromises.has(cacheKey)
        ) {
            return this._loadingPromises.get(cacheKey);
        }

        this._isLoading.set(cacheKey, true);
        const promise = this._fetchBanners(filter, cacheKey, authMode);
        this._loadingPromises.set(cacheKey, promise);

        return promise;
    }

    private async _fetchBanners(
        filter: ModelHomePageBottomBannerFilterInput,
        cacheKey: string,
        authMode?: ListGraphqlAuthMode
    ): Promise<HomePageBottomBanner[]> {
        try {
            const data = ((await this._amplifyApiService.graphql({
                query: queries.listHomePageBottomBanners,
                variables: {
                    filter
                },
                ...(authMode ? { authMode } : {})
            })) as any).data.listHomePageBottomBanners.items;

            if (data) {
                const banners = data as HomePageBottomBanner[];
                this._cachedBanners.set(cacheKey, banners);
                this._isLoading.set(cacheKey, false);
                this._loadingPromises.delete(cacheKey);
                return banners;
            }
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        } catch (error) {
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            console.error('Error finding HomePageBottomBanners:', error);
            return null;
        }
    }

    clearCache(): void {
        this._cachedBanners.clear();
        this._isLoading.clear();
        this._loadingPromises.clear();
    }

    async getHomePageBottomBanner(id: string): Promise<HomePageBottomBanner> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getHomePageBottomBanner,
                    variables: {
                        id
                    }
                })
            ).data.getHomePageBottomBanner;
            if (data) return data as HomePageBottomBanner;
        } catch (error) {
            console.error('Error finding HomePageBottomBanner:', error);
            return null;
        }
    }

    async createHomePageBottomBanner(
        input: CreateHomePageBottomBannerInput
    ): Promise<HomePageBottomBanner> {
        try {
            const date = (
                await this.amplifyClient.graphql({
                    query: mutations.createHomePageBottomBanner,
                    variables: {
                        input
                    }
                })
            ).data.createHomePageBottomBanner;
            if (date) return date as HomePageBottomBanner;
        } catch (error) {
            console.error('Error creating HomePage:', error);
            return null;
        }
    }

    async updateHomePageBottomBanner(
        input: UpdateHomePageBottomBannerInput
    ): Promise<HomePageBottomBanner> {
        try {
            const date = (
                await this.amplifyClient.graphql({
                    query: mutations.updateHomePageBottomBanner,
                    variables: {
                        input
                    }
                })
            ).data.updateHomePageBottomBanner;
            if (date) return date as HomePageBottomBanner;
        } catch (error) {
            console.error('Error updating HomePage:', error);
            return null;
        }
    }

    async deleteHomePageBottomBanner(
        input: DeleteHomePageBottomBannerInput
    ): Promise<HomePageBottomBanner> {
        try {
            const date = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteHomePageBottomBanner,
                    variables: {
                        input
                    }
                })
            ).data.deleteHomePageBottomBanner;
            if (date) return date as HomePageBottomBanner;
        } catch (error) {
            console.error('Error deleting HomePage:', error);
            return null;
        }
    }
}
